import { TableTemplate } from '@repo/types';
import type { Table } from '@tanstack/react-table';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components';
import { Settings2Icon, Trash2Icon } from 'lucide-react';

interface ViewButtonProps<TData> {
  table: Table<TData>;
  selectedTemplateId?: number | null;
  templates?: TableTemplate[];
  onSelectTemplate?: (template: TableTemplate) => void;
  onDeleteTemplate?: (template: TableTemplate) => void;
}

export function ViewButton<TData>({
  table,
  selectedTemplateId,
  templates,
  onSelectTemplate,
  onDeleteTemplate,
}: ViewButtonProps<TData>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Toggle columns"
          className="ml-auto h-10"
          size="sm"
          variant="outline"
        >
          <Settings2Icon className="mr-2 size-4" />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-96 overflow-auto"
      >
        {!!templates?.length && (
          <>
            <DropdownMenuLabel>Templates</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {templates?.map((template) => (
              <DropdownMenuCheckboxItem
                key={template.id}
                checked={selectedTemplateId === template.id}
                className="group flex items-center justify-between"
                onSelect={(e) => {
                  e.preventDefault();
                  onSelectTemplate?.(template);
                }}
              >
                {template.title}

                <div
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  role="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onDeleteTemplate?.(template);
                  }}
                >
                  <Trash2Icon className="size-4 text-destructive" />
                </div>
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter((column) => column.getCanHide() && column.id !== 'actions')
          .map((column) => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                checked={column.getIsVisible()}
                className="capitalize"
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                onSelect={(e) => e.preventDefault()}
              >
                {column.columnDef.meta?.name ?? column.id}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
